import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Star rating display component
const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? 'text-yellow-400 fill-yellow-400'
              : 'text-gray-300 dark:text-silver/30'
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// Loading skeleton for review cards
const ReviewCardSkeleton = () => (
  <div className="glass border border-light-border dark:border-border rounded-2xl p-6 animate-pulse">
    <div className="flex justify-between items-start mb-4">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="w-4 h-4 bg-slate-200 dark:bg-silver/20 rounded"></div>
        ))}
      </div>
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-20"></div>
    </div>
    <div className="h-16 bg-slate-200 dark:bg-silver/20 rounded w-full mb-4"></div>
    <div className="flex justify-between items-center">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
      <div className="flex gap-2">
        <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-20"></div>
        <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-20"></div>
      </div>
    </div>
  </div>
);

const AdminReviews = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [moderatingId, setModeratingId] = useState(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'admin')) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const fetchReviews = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'admin' || !token) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/admin/reviews/pending`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (response.status === 403) {
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch pending reviews');
      }

      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, token, navigate]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleModerate = async (reviewId, status) => {
    setModeratingId(reviewId);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/admin/reviews/${reviewId}/moderate`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        },
        15000
      );

      if (!response.ok) {
        throw new Error(`Failed to ${status} review`);
      }

      // Remove the moderated review from the list
      setReviews(reviews.filter(r => r.id !== reviewId));
    } catch (error) {
      console.error('Error moderating review:', error);
      setApiError(error);
    } finally {
      setModeratingId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/admin/dashboard"
            className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
          >
            &larr; Back to Dashboard
          </Link>
        </div>
        <h1 className="font-display text-4xl italic tracking-tighter mb-2">
          Review Moderation
        </h1>
        <p className="font-body text-light-text-secondary dark:text-silver/60">
          Approve or reject pending patient reviews
        </p>
      </div>

      {/* API Error Alert */}
      {apiError && (
        <div className="mb-6">
          <ApiErrorAlert
            error={apiError}
            onRetry={fetchReviews}
          />
        </div>
      )}

      {/* Stats Summary */}
      <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
        <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
          Pending Reviews
        </p>
        <p className="font-display text-3xl italic">{reviews.length}</p>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {isLoading ? (
          <>
            <ReviewCardSkeleton />
            <ReviewCardSkeleton />
            <ReviewCardSkeleton />
          </>
        ) : reviews.length === 0 ? (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-12 text-center">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="font-display text-xl italic mb-2">All Caught Up!</h3>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              No pending reviews to moderate
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <div
              key={review.id}
              className="glass border border-light-border dark:border-border rounded-2xl p-6"
            >
              {/* Review Header */}
              <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                <div>
                  <StarRating rating={review.rating} />
                  <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                    {review.created_at
                      ? new Date(review.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                      : 'Unknown date'}
                  </p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono uppercase bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                  Pending
                </span>
              </div>

              {/* Review Content */}
              <p className="font-body text-light-text dark:text-silver mb-4">
                {review.comment || <em className="text-light-text-secondary dark:text-silver/60">No comment provided</em>}
              </p>

              {/* Review Meta */}
              <div className="flex flex-wrap justify-between items-center gap-4 pt-4 border-t border-light-border dark:border-border">
                <div className="flex flex-col sm:flex-row sm:gap-4">
                  <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                    <span className="uppercase">Patient:</span>{' '}
                    <span className="text-light-text dark:text-silver">
                      {review.patient_first_name} {review.patient_last_name}
                    </span>
                  </p>
                  <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                    <span className="uppercase">Doctor:</span>{' '}
                    <span className="text-light-text dark:text-silver">
                      Dr. {review.doctor_first_name} {review.doctor_last_name}
                    </span>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleModerate(review.id, 'approved')}
                    disabled={moderatingId === review.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs uppercase tracking-widest bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
                  >
                    {moderatingId === review.id ? (
                      <span className="animate-spin h-3 w-3 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full"></span>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => handleModerate(review.id, 'rejected')}
                    disabled={moderatingId === review.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs uppercase tracking-widest bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
                  >
                    {moderatingId === review.id ? (
                      <span className="animate-spin h-3 w-3 border-2 border-red-600 dark:border-red-400 border-t-transparent rounded-full"></span>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
